import React, { useMemo, type ComponentProps } from 'react';
import c from 'classnames';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useWindowDimensions, Dialog } from '@uncinc/uncinc-react-kitchen-sink';

import { useDialogDrawerContext } from '../../context/DialogDrawerContext';
import Button from '../Button';

import './style.scss';

// Subcomponents.
import DialogDrawerButton from './Button';
import { SCREEN_TABLET } from '../../config/constants';

interface Subcomponents {
  Button: typeof DialogDrawerButton;
}

export interface DialogDrawerProps extends ComponentProps<typeof Dialog> {
  className?: string;
  disableClosers?: boolean;
  children: React.ReactNode;
}

interface BackdropComponentProps extends Omit<ComponentProps<typeof motion.div>, 'animate' | 'children' | 'exit' | 'initial'> {
  children: React.ReactNode;
}

interface WrapperComponentProps extends Omit<BackdropComponentProps, 'ref'> {
  forwardRef: React.RefAttributes<HTMLDivElement>[`ref`];
}

const WrapperComponentRight: React.FC<WrapperComponentProps> = ({
  forwardRef, children, ...props
}) => (
  <motion.div
    {...props}
    initial={{ x: '100%' }}
    animate={{ x: 0, transition: { duration: 0.5, ease: 'circOut' } }}
    exit={{ x: '100%' }}
    ref={forwardRef}
  >
    {children}
  </motion.div>
);

const WrapperComponentBottom: React.FC<WrapperComponentProps> = ({
  forwardRef, children, ...props
}) => (
  <motion.div
    {...props}
    initial={{ y: '100%' }}
    animate={{ y: 0, transition: { duration: 0.5, ease: 'circOut' } }}
    exit={{ y: '100%' }}
    ref={forwardRef}
  >
    {children}
  </motion.div>
);

const BackdropComponent: React.FC<BackdropComponentProps> = ({ children, ...props }) => (
  <motion.div
    {...props}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    {children}
  </motion.div>
);

const DialogDrawer: React.FC<DialogDrawerProps> & Subcomponents = (props) => {
  const { t } = useTranslation();
  const {
    dialogIsOpen,
    DialogComponent,
    closeDialog,
  } = useDialogDrawerContext();

  const {
    className,
    children,
    disableClosers,
    ...restProps
  } = props;

  const classNames = c('DialogDrawer', className);
  const { width } = useWindowDimensions();
  const isMobile = useMemo(() => (width < SCREEN_TABLET), [width]);

  return (
    <MotionConfig transition={{ duration: 0.5, ease: 'easeOut' }}>
      <AnimatePresence>
        {(dialogIsOpen && (
          <DialogComponent
            {...restProps}
            className={classNames}
            wrapperComponent={isMobile ? WrapperComponentBottom : WrapperComponentRight}
            backdropComponent={BackdropComponent}
            portalTarget={document.querySelector('.theme')}
            disableClosers={disableClosers}
          >
            <div className="DialogDrawer__header">
              <Button type="transparent" className="DialogDrawer__back-button" onClick={closeDialog}>
                <i className="icon icon-chevron-right icon-orientation-left" />
                <span>{t('general.back')}</span>
              </Button>
            </div>

            {children}
          </DialogComponent>
        ))}
      </AnimatePresence>
    </MotionConfig>
  );
};

DialogDrawer.Button = DialogDrawerButton;

export default DialogDrawer;
